import { startsWith, get, trimStart } from "lodash";

import { Group, GroupCode, GroupDomain, GroupUser } from "../models";

import { subscriptionsAPI } from "services/apis.service";

export class GroupService {
  #axios;
  constructor(axiosInstance) {
    this.#axios = axiosInstance;
  }

  async getGroup() {
    try {
      const { data } = await this.#axios.get(subscriptionsAPI.getGroupData, {
        cache: false,
      });
      if (data === "") {
        return null;
      }
      return new Group(data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async getUsers(page, pageSize, filterModel, sortModel) {
    let filterValue = get(filterModel, "value", null);
    if (startsWith(filterValue, "@") && filterModel.field === "userName") {
      filterValue = trimStart(filterValue, "@");
    }
    try {
      const { data } = await this.#axios.post(
        subscriptionsAPI.getGroupUsers,
        {
          paginate: true,
          pageIndex: page + 1,
          pageSize,
          filterField: get(filterModel, "field", null),
          filterOperator: get(filterModel, "operator", null),
          filterValue,
          sortField: get(sortModel, "field", null),
          sortOrder: get(sortModel, "sort", null),
        },
        {
          cache: false,
        }
      );
      if (data == null) {
        return {
          users: [],
          page,
          pageSize,
          total: 0,
        };
      }
      const users = data.users.map((user) => new GroupUser(user));
      return {
        users,
        total: data.pagination.totalCount,
        page: data.pagination.pageIndex - 1,
        pageSize: data.pagination.pageSize,
      };
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async getGroupCodes() {
    try {
      const response = await this.#axios.get(subscriptionsAPI.getGroupCodes, {
        cache: false,
      });
      return response.data.map((code) => new GroupCode(code));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async createGroupCode() {
    try {
      const response = await this.#axios.post(
        subscriptionsAPI.createGroupCode,
        {},
        { cache: false }
      );
      return new GroupCode(response.data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async deleteGroupCode(groupCodeId) {
    try {
      await this.#axios.delete(
        `${subscriptionsAPI.deleteGroupCode}?codeId=${groupCodeId}`
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async joinGroupCode(code) {
    try {
      const response = await this.#axios.post(
        subscriptionsAPI.joinGroupWithCode,
        {
          code,
        },
        {
          cache: false,
        }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      if (error.response.data) {
        return {
          status: "error",
          error: error.response.data,
        };
      }
      return {
        status: "error",
        error,
      };
    }
  }

  async getGroupDomains() {
    try {
      const { data } = await this.#axios.get(subscriptionsAPI.getGroupDomains, {
        cache: false,
      });
      return data.map((domain) => new GroupDomain(domain));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async addUpdateDomain(id, domain) {
    try {
      const { data } = await this.#axios.post(
        subscriptionsAPI.createUpdateGroupDomain,
        {
          domainId: id,
          domain,
        }
      );
      if (data.ErrorMessage != null) {
        throw new Error(data.ErrorMessage[0]);
      }
      return new GroupDomain(data);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async deleteDomain(id) {
    try {
      await this.#axios.delete(
        `${subscriptionsAPI.deleteGroupDomain}?domainId=${id}`
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateOrganizationClassLinkSync(sync) {
    try {
      await this.#axios.post(
        subscriptionsAPI.updateOrganizationClassLinkSync,
        {
          isClassLinkSyncEnabled: sync,
        },
        { cache: false }
      );
      return {
        success: true,
      };
    } catch (error) {
      console.error(error);
      return {
        success: false,
        error,
      };
    }
  }

  async getSourceIdByClassLink() {
    try {
      const response = await this.#axios.get(
        subscriptionsAPI.getSourceIdByClassLink, // + '?simulateProxyAuthFailure=true',
        { cache: false }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateSyncedClassLinkSources(sourceIds) {
    try {
      const response = await this.#axios.post(
        subscriptionsAPI.addRemoveClassLinkSourceByOrgId,
        { sourceIds: [...sourceIds] },
        { cache: false }
      );
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}
